import { useCallback, useEffect, useRef, useState } from 'react';

import './IntroReel.css';

import MiaSignature from '@/components/MiaSignature';
import ReelToys from './ReelToys';

import welcomeMumBg from '@/assets/images/intro-reel/welcome-mum-bg.jpg';
import playroomBg from '@/assets/images/intro-reel/playroom-bg.jpg';
import projectionBg from '@/assets/images/intro-reel/projection-bg.jpg';
import family4Bg from '@/assets/images/intro-reel/family-4-bg.jpg';
import fscsLogo from '@/assets/images/fscs-logo.svg';
import avivaLogo from '@/assets/images/aviva-logo.svg';
import fidelityLogo from '@/assets/images/fidelity-logo.svg';

import {
  BEAT_DURATIONS_MS,
  LAST_BEAT,
  MESSAGE_DELAY_MS,
  PROJECTION_DISCLAIMER_BODY,
  PROJECTION_DISCLAIMER_RISK,
  REEL_COPY,
  REEL_FIGURES,
  REEL_GIFTS,
  REEL_TOTAL_MS,
} from '@/content/intro-reel';

const BACKGROUNDS = [welcomeMumBg, playroomBg, projectionBg, family4Bg];

/** Rolls a counter to its target with the handoff's ease-out cubic. */
function useCountUp(target: number, from: number, durationMs: number, run: boolean) {
  const [value, setValue] = useState(from);
  const [running, setRunning] = useState(run);

  // Adjusting during render rather than in an effect: the counter must already
  // read its starting figure on the pass that first shows the beat.
  if (running !== run) {
    setRunning(run);
    setValue(from);
  }

  useEffect(() => {
    if (!run) return;
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, from, durationMs, run]);

  return value;
}

type IntroReelProps = {
  /** Called when the last beat ends, or the viewer skips (AC 3). */
  onFinish: () => void;
};

/**
 * The Meet Mia reel (GIFTWEB-28) — four beats over 22.4 seconds, ported from
 * `.claude/AlexGifting/handoff-intro-reel/`.
 *
 * The standalone reel loops. Here it plays once and calls `onFinish`, because
 * the overlay it lives in is meant to close when the reel ends.
 */
function IntroReel({ onFinish }: IntroReelProps) {
  const [beat, setBeat] = useState(1);
  const finished = useRef(false);

  const finish = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    onFinish();
  }, [onFinish]);

  // Auto-advance. Tapping (below) resets this by changing `beat`.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (beat >= LAST_BEAT) finish();
      else setBeat((current) => current + 1);
    }, BEAT_DURATIONS_MS[beat]);
    return () => clearTimeout(timer);
  }, [beat, finish]);

  /**
   * Beat 4 plays a small sequence of its own: the cards stack in, then a
   * typing bubble gives way to the written message. State rather than pure
   * CSS because the two share one slot — animating them past each other
   * would shift everything below.
   */
  const [messageArrived, setMessageArrived] = useState(false);
  useEffect(() => {
    // No reset branch: the beat only ever moves forward, and reopening the
    // reel mounts a fresh one.
    if (beat !== LAST_BEAT) return;
    const timer = setTimeout(() => setMessageArrived(true), MESSAGE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [beat]);

  /** AC 4 — a tap moves the story on; a tap on the last beat ends it. */
  const advance = () => {
    if (beat >= LAST_BEAT) finish();
    else setBeat((current) => current + 1);
  };

  const projection = useCountUp(
    REEL_FIGURES.projectionMedium,
    REEL_FIGURES.projectionFrom,
    1_700,
    beat === 3,
  );

  /** Exactly as briefed — £12,987 and £24,004 are not round numbers. */
  const money = (value: number) => `£${value.toLocaleString('en-GB')}`;

  /** The rolling counter lands on hundreds while it moves, as the app's does. */
  const rollingMoney = (value: number) => money(Math.round(value / 100) * 100);

  return (
    <div className='relative h-full w-full overflow-hidden bg-[#1a201d] font-sans'>
      {/* Ken Burns backgrounds, cross-fading per beat. */}
      <div aria-hidden='true' className='absolute inset-0 z-0 overflow-hidden'>
        {BACKGROUNDS.map((src, index) => (
          <img
            key={src}
            src={src}
            alt=''
            className='reel-bg'
            data-active={beat === index + 1}
          />
        ))}
        <div className='absolute inset-0 bg-[linear-gradient(180deg,rgba(16,32,25,0.62)_0%,rgba(16,32,25,0.34)_32%,rgba(16,32,25,0.42)_60%,rgba(16,32,25,0.78)_100%)]' />
      </div>

      {/* Toys pour in on beat 2 and puff away on beat 3, as in the handoff. */}
      {(beat === 2 || beat === 3) && <ReelToys puffing={beat === 3} />}

      {/* The projection graph draws in behind the figures. */}
      {beat === 3 && (
        <div
          aria-hidden='true'
          className='reel-graph pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-1/2'
        >
          <svg viewBox='0 0 300 150' preserveAspectRatio='none' className='absolute inset-0 size-full'>
            <path
              className='reel-graph-area'
              d='M0 150 L0 122 C40 117 90 109 150 107 C182 106 184 92 212 84 C252 72 282 64 300 58 L300 150 Z'
              fill='rgba(127,184,160,0.14)'
            />
            <path
              className='reel-graph-line'
              d='M0 122 C40 117 90 109 150 107 C182 106 184 92 212 84 C252 72 282 64 300 58'
              fill='none'
              stroke='rgba(159,224,198,0.5)'
              strokeWidth='2.5'
              strokeLinecap='round'
              strokeLinejoin='round'
              pathLength={340}
              strokeDasharray={340}
            />
            <path
              className='reel-graph-line'
              d='M0 120 C40 114 90 104 150 102 C180 101 182 80 210 66 C250 46 280 36 300 30'
              fill='none'
              stroke='#9fe0c6'
              strokeWidth='3.5'
              strokeLinecap='round'
              strokeLinejoin='round'
              pathLength={340}
              strokeDasharray={340}
            />
            <path
              className='reel-graph-line'
              d='M0 118 C40 110 90 96 150 92 C180 90 176 58 210 44 C250 24 280 12 300 6'
              fill='none'
              stroke='rgba(159,224,198,0.5)'
              strokeWidth='2.5'
              strokeLinecap='round'
              strokeLinejoin='round'
              pathLength={340}
              strokeDasharray={340}
            />
          </svg>
        </div>
      )}

      {/* Tap anywhere to advance (AC 4). A button, so it is reachable by keyboard. */}
      <button
        type='button'
        onClick={advance}
        aria-label='Next scene'
        className='absolute inset-0 z-[4] cursor-pointer'
      />

      <div className='pointer-events-none absolute inset-0 z-[5] flex flex-col items-center justify-center gap-[22px] p-6 text-center text-white'>
        {/* Persists across every beat, as it does in the handoff. */}
        <div className='reel-beat flex items-center gap-[9px]'>
          <span className='relative top-0.5 text-[20px] whitespace-nowrap text-white/90 drop-shadow-[0_1px_10px_rgba(0,0,0,0.4)]'>
            Hi, I'm
          </span>
          <span className='relative -top-1 leading-[0]'>
            <MiaSignature height={36} color='#ffffff' />
          </span>
        </div>

        {beat === 1 && (
          <div className='reel-beat'>
            <p className='text-[19px] font-semibold drop-shadow-[0_1px_14px_rgba(0,0,0,0.45)]'>
              {REEL_COPY.beat1.headline}
            </p>

            {/* The two products the gift can go into. */}
            <div className='mt-4 flex flex-wrap items-center justify-center gap-2'>
              {REEL_COPY.beat1.chips.map((chip) => (
                <span
                  key={chip}
                  className='rounded-full border border-white/30 bg-white/10 px-3 py-[5px] text-[11px] font-semibold text-white/95 backdrop-blur-[2px]'
                >
                  {chip}
                </span>
              ))}
            </div>

            {/*
              Trust panel. The brief's wording and its three logos, which
              differ from the reference recording — that shows "Your money is
              managed by" with Aviva and Fidelity only. Agreed to follow the
              brief; see the note in content/intro-reel.ts.
            */}
            <div className='mx-auto mt-3.5 w-fit rounded-xl border border-white/15 bg-[rgba(13,29,22,0.55)] px-3.5 py-2.5 backdrop-blur-[3px]'>
              <p className='text-[10px] font-semibold tracking-[0.02em] text-white/75'>
                {REEL_COPY.beat1.trust}
              </p>
              <div className='mt-2 flex items-center justify-center gap-[11px]'>
                <img src={fscsLogo} alt='FSCS' className='h-[15px] w-auto brightness-0 invert' />
                <span className='h-3.5 w-px bg-white/30' />
                <img src={avivaLogo} alt='Aviva' className='h-[11px] w-auto brightness-0 invert' />
                <span className='h-3.5 w-px bg-white/30' />
                <img src={fidelityLogo} alt='Fidelity' className='h-[13px] w-auto brightness-0 invert' />
              </div>
            </div>
          </div>
        )}

        {/*
          Three lines with the middle one carrying the weight. The August
          handoff rolled a counter to 13,000 here; the brief replaces the
          figure with the word, so there is nothing left to count.
        */}
        {beat === 2 && (
          <div className='reel-beat'>
            <p className='text-[19px] font-semibold drop-shadow-[0_1px_14px_rgba(0,0,0,0.45)]'>
              {REEL_COPY.beat2.lead}
            </p>
            <p className='mt-1 text-[48px] leading-none font-bold tracking-[-0.02em] drop-shadow-[0_1px_18px_rgba(0,0,0,0.5)]'>
              {REEL_COPY.beat2.emphasis}
            </p>
            <p className='mt-1.5 text-[19px] font-semibold drop-shadow-[0_1px_14px_rgba(0,0,0,0.45)]'>
              {REEL_COPY.beat2.tail}
            </p>
          </div>
        )}

        {beat === 3 && (
          <div className='reel-beat'>
            <p className='mb-3.5 text-[13.5px] text-white/90'>{REEL_COPY.beat3.lead}</p>
            <div className='flex items-end justify-center gap-3.5'>
              <Figure label='Low' value={money(REEL_FIGURES.projectionLow)} />
              <div className='text-center'>
                <div className='mb-[3px] text-[10.5px] font-bold tracking-[0.05em] text-[#9fe0c6] uppercase'>
                  Medium
                </div>
                <div className='text-[42px] leading-none font-bold tracking-[-0.02em] drop-shadow-[0_1px_18px_rgba(0,0,0,0.5)]'>
                  {rollingMoney(projection)}
                </div>
              </div>
              <Figure label='High' value={money(REEL_FIGURES.projectionHigh)} />
            </div>
            <p className='mt-3 text-[14px] font-semibold drop-shadow-[0_1px_14px_rgba(0,0,0,0.45)]'>
              {REEL_COPY.beat3.tagline}
            </p>
          </div>
        )}

        {beat === 4 && (
          <div className='reel-beat w-full'>
            <p className='mb-3.5 text-[19px] font-semibold tracking-[-0.01em] drop-shadow-[0_1px_14px_rgba(0,0,0,0.45)]'>
              {REEL_COPY.beat4.headline}
            </p>

            <div className='flex flex-col gap-2'>
              {/* The voice note, first in and first to play. */}
              <div
                className='reel-gift flex items-center gap-2.5 rounded-[13px] bg-white px-3 py-2.5 shadow-[0_8px_22px_rgba(16,32,25,0.28)]'
                style={{ animationDelay: '0.1s' }}
              >
                <span className='flex size-8 shrink-0 items-center justify-center rounded-full bg-[#256956]'>
                  <svg width='13' height='13' viewBox='0 0 24 24' fill='#fff' aria-hidden='true'>
                    <path d='M8 5v14l11-7z' />
                  </svg>
                </span>
                <span className='min-w-0 flex-1'>
                  <span className='block truncate text-center text-[11px] font-semibold text-[#3a423d]'>
                    {REEL_COPY.beat4.voiceFrom}
                  </span>
                  <span className='mt-[5px] flex h-3.5 items-center justify-center gap-[2px]'>
                    {Array.from({ length: 18 }, (_, i) => (
                      <span
                        key={i}
                        className='reel-wave w-[2.5px] rounded-[2px] bg-[#256956]'
                        style={{ height: `${35 + ((i * 37) % 65)}%`, animationDelay: `${i * 60}ms` }}
                      />
                    ))}
                  </span>
                </span>
                <span className='shrink-0 text-[11px] text-[#656e68] tabular-nums'>
                  {REEL_COPY.beat4.voiceDuration}
                </span>
              </div>

              {/* Two gifts land, then the note, then the third. */}
              {REEL_GIFTS.slice(0, 2).map((gift, index) => (
                <GiftRow key={gift.name} gift={gift} delay={`${0.35 + index * 0.25}s`} />
              ))}

              {messageArrived ? (
                <div
                  className='reel-gift rounded-[13px] bg-[#dbe7ef] px-3 py-2 text-left text-[12px] leading-[1.4] text-[#0f2922] shadow-[0_8px_22px_rgba(16,32,25,0.24)]'
                >
                  {REEL_COPY.beat4.message}
                </div>
              ) : (
                <div
                  aria-hidden='true'
                  data-testid='reel-typing'
                  className='reel-gift flex w-fit items-center gap-[5px] rounded-[13px] bg-white/90 px-3 py-2.5 shadow-[0_8px_22px_rgba(16,32,25,0.2)]'
                  style={{ animationDelay: '0.9s' }}
                >
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className='reel-typing-dot size-[5px] rounded-full bg-[#8a938c]'
                      style={{ animationDelay: `${i * 160}ms` }}
                    />
                  ))}
                </div>
              )}

              {messageArrived && (
                <GiftRow gift={REEL_GIFTS[2]} delay='0.25s' />
              )}
            </div>

            <p className='mt-3 text-[11px] text-white/85 drop-shadow-[0_1px_10px_rgba(0,0,0,0.5)]'>
              {REEL_COPY.beat4.unlock}
            </p>
          </div>
        )}
      </div>

      {/* AC 6 — on screen for exactly as long as the projection is. */}
      {beat === 3 && (
        <div className='pointer-events-none absolute inset-x-3.5 bottom-3 z-[6] flex justify-center'>
          <p className='max-w-[300px] rounded-[10px] bg-[rgba(13,29,22,0.86)] px-3 py-2 text-center text-[10.5px] leading-[1.45] font-medium text-[#dcefe4]'>
            {PROJECTION_DISCLAIMER_BODY}{' '}
            {/* Bold is a compliance requirement here, not emphasis. */}
            <strong className='font-bold text-white'>
              {PROJECTION_DISCLAIMER_RISK}
            </strong>
          </p>
        </div>
      )}

      {/* Close, carrying the design's countdown across the whole reel. */}
      <button
        type='button'
        onClick={finish}
        className='absolute top-3 right-2.5 z-[7] flex items-center gap-2 rounded-full bg-black/25 px-3 py-1.5 text-[12px] font-semibold text-white/85'
      >
        <span className='block h-[3px] w-14 overflow-hidden rounded-sm bg-white/30'>
          <span
            className='reel-countdown block h-full w-full bg-white/80'
            style={{ animationDuration: `${REEL_TOTAL_MS}ms` }}
          />
        </span>
        Close
      </button>
    </div>
  );
}

function GiftRow({
  gift,
  delay,
}: {
  gift: (typeof REEL_GIFTS)[number];
  delay: string;
}) {
  return (
    <div
      className='reel-gift flex items-center gap-2.5 rounded-[13px] bg-white px-3 py-2 shadow-[0_8px_22px_rgba(16,32,25,0.28)]'
      style={{ animationDelay: delay }}
    >
      <span
        className='flex size-9 shrink-0 items-center justify-center rounded-full text-[11px] font-bold'
        style={{ background: gift.bg, color: gift.fg }}
      >
        {gift.initials}
      </span>
      <span className='min-w-0 flex-1 text-center'>
        <span className='block text-[10.5px] text-[#656e68]'>Gift from</span>
        <span className='block truncate text-[13px] font-bold text-[#0f2922]'>
          {gift.name}
        </span>
      </span>
      <span className='shrink-0 text-[15px] font-bold text-[#256956]'>
        {gift.amount}
      </span>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className='text-center'>
      <div className='mb-[3px] text-[10.5px] font-bold tracking-[0.05em] text-white/65 uppercase'>
        {label}
      </div>
      <div className='text-[24px] leading-none font-bold tracking-[-0.02em] text-white/[0.78] drop-shadow-[0_1px_14px_rgba(0,0,0,0.5)]'>
        {value}
      </div>
    </div>
  );
}

export default IntroReel;
